type LogLevel = "debug" | "info" | "warn" | "error" | "fatal";
type LogContext = Record<string, unknown>;

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  fatal: 4,
};

const COLORS: Record<LogLevel, string> = {
  debug: "\x1b[36m",
  info: "\x1b[32m",
  warn: "\x1b[33m",
  error: "\x1b[31m",
  fatal: "\x1b[35m",
};

const RESET = "\x1b[0m";

const IS_PRODUCTION = process.env.NODE_ENV === "production";
const CURRENT_LEVEL = (process.env.LOG_LEVEL || (IS_PRODUCTION ? "warn" : "info")) as LogLevel;
const SERVICE_NAME = process.env.SERVICE_NAME || "retailos";

function shouldLog(level: LogLevel): boolean {
  return LOG_LEVELS[level] >= LOG_LEVELS[CURRENT_LEVEL];
}

function formatError(error: unknown): Record<string, unknown> {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
      ...Object.getOwnPropertyNames(error).reduce(
        (acc, key) => {
          if (!["name", "message", "stack"].includes(key)) {
            acc[key] = (error as unknown as Record<string, unknown>)[key];
          }
          return acc;
        },
        {} as Record<string, unknown>,
      ),
    };
  }
  return { message: String(error) };
}

function writeLog(level: LogLevel, message: string, context?: LogContext) {
  if (!shouldLog(level)) return;

  if (IS_PRODUCTION) {
    const entry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      service: SERVICE_NAME,
      environment: process.env.NODE_ENV || "development",
      ...context,
    };
    const output = JSON.stringify(entry);
    if (level === "error" || level === "fatal") {
      console.error(output);
    } else {
      console.log(output);
    }
  } else {
    const color = COLORS[level];
    const timestamp = new Date().toISOString().slice(11, 23);
    const prefix = `${color}[${timestamp}] ${level.toUpperCase().padEnd(5)}${RESET}`;
    if (context && Object.keys(context).length > 0) {
      console.log(`${prefix} ${message}`, context);
    } else {
      console.log(`${prefix} ${message}`);
    }
  }
}

function createLogger(defaultContext?: LogContext) {
  return {
    debug: (message: string, context?: LogContext) =>
      writeLog("debug", message, { ...defaultContext, ...context }),
    info: (message: string, context?: LogContext) =>
      writeLog("info", message, { ...defaultContext, ...context }),
    warn: (message: string, context?: LogContext) =>
      writeLog("warn", message, { ...defaultContext, ...context }),
    error: (message: string, context?: LogContext) =>
      writeLog("error", message, { ...defaultContext, ...context }),
    fatal: (message: string, context?: LogContext) =>
      writeLog("fatal", message, { ...defaultContext, ...context }),
    child: (childContext: LogContext) =>
      createLogger({ ...defaultContext, ...childContext }),
    async time<T>(label: string, fn: () => Promise<T>, context?: LogContext): Promise<T> {
      const start = Date.now();
      try {
        const result = await fn();
        writeLog("info", `${label} completed in ${Date.now() - start}ms`, {
          ...defaultContext,
          ...context,
          durationMs: Date.now() - start,
        });
        return result;
      } catch (error) {
        writeLog("error", `${label} failed after ${Date.now() - start}ms`, {
          ...defaultContext,
          ...context,
          durationMs: Date.now() - start,
          error: formatError(error),
        });
        throw error;
      }
    },
  };
}

export const logger = createLogger();
export type Logger = ReturnType<typeof createLogger>;
